import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { getTournamentQueryOptions } from '@/features/tournaments/api/get-tournament';

import { courtsLoader as commentatorsCourtsLoader } from './app/commentators/courts';
import { lineupsLoader } from './app/commentators/lineups';
import { courtLoader } from './app/courts/court';
import { courtsLoader } from './app/courts/courts';

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const createRouter = (queryClient: QueryClient) => {
  return sentryCreateBrowserRouter([
    {
      path: '',
      children: [
        { index: true, element: <Navigate to="/courts" /> },
        {
          path: '/commentators',
          lazy: async () => {
            const { CommentatorsPage } = await import(
              './app/commentators/commentators'
            );
            return { Component: CommentatorsPage };
          },
        },
        {
          path: '/commentators/divisions/:divisionId',
          lazy: async () => {
            const { CommentatorsDivisionsPage } = await import(
              './app/commentators/divisions'
            );
            return { Component: CommentatorsDivisionsPage };
          },
        },
        {
          path: '/commentators/divisions/:divisionId/teams/:teamId',
          lazy: async () => {
            const { CommentatorsTeamPage } = await import(
              './app/commentators/team'
            );
            return { Component: CommentatorsTeamPage };
          },
        },
        {
          path: '/commentators/courts',
          lazy: async () => {
            const { CommentatorsCourtsPage } = await import(
              './app/commentators/courts'
            );
            return { Component: CommentatorsCourtsPage };
          },
          loader: commentatorsCourtsLoader(queryClient),
        },
        {
          path: '/commentators/courts/:courtId/lineups',
          lazy: async () => {
            const { CommentatorsLineupsPage } = await import(
              './app/commentators/lineups'
            );
            return { Component: CommentatorsLineupsPage };
          },
          loader: lineupsLoader(queryClient),
        },
        {
          path: '/courts',
          lazy: async () => {
            const { CourtsPage } = await import('./app/courts/courts');
            return { Component: CourtsPage };
          },
          loader: courtsLoader(queryClient),
        },
        {
          path: '/courts/:courtId',
          lazy: async () => {
            const { CourtPage } = await import('./app/courts/court');
            return { Component: CourtPage };
          },
          loader: courtLoader(queryClient),
        },
        {
          path: '/courts/:courtId/control',
          lazy: async () => {
            const { CourtControlPage } = await import('./app/courts/control');
            return { Component: CourtControlPage };
          },
          loader: courtLoader(queryClient),
        },
        {
          path: '/courts/:courtId/tv',
          lazy: async () => {
            const { StreamOverlayPage } = await import(
              './app/courts/stream-overlay'
            );
            return { Component: StreamOverlayPage };
          },
          loader: courtLoader(queryClient),
        },
        {
          path: '*',
          lazy: async () => {
            const { NotFoundRoute } = await import('./not-found');
            return { Component: NotFoundRoute };
          },
        },
      ],
      loader: async () => {
        const query = getTournamentQueryOptions();

        return (
          queryClient.getQueryData(query.queryKey) ??
          (await queryClient.fetchQuery(query))
        );
      },
    },
  ]);
};
